import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { roleLabel } from '../utils/roles';

export default function Profile() {
  const { user, checkAuth } = useAuth();
  const [form, setForm] = useState({ name: user?.name || '', email: user?.email || '', language: user?.language || 'pt' });
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  // Conta vinda do Microsoft: nome e e-mail sao o que assina revisao,
  // aprovacao e leitura, entao a fonte da verdade e o tenant. O servidor
  // recusa a alteracao de qualquer jeito; aqui evitamos oferecer um campo
  // que so devolveria erro.
  const identidadeDoTenant = Boolean(user?.azure_oid);

  // Conta federada nao tem senha local: a dela vive no Microsoft 365, e o
  // servidor recusa a troca com SSO_ACCOUNT. Oferecer o formulario so levaria
  // a pessoa a preencher tres campos para receber um erro.
  const temSenhaLocal = Boolean(user?.has_local_password);

  const [senha, setSenha] = useState({ atual: '', nova: '', confirmacao: '' });
  const [trocando, setTrocando] = useState(false);
  const [erroSenha, setErroSenha] = useState('');
  const [okSenha, setOkSenha] = useState('');

  async function handleTrocarSenha(e) {
    e.preventDefault(); setErroSenha(''); setOkSenha('');

    // Conferido aqui porque o servidor nao recebe a confirmacao — ele nao tem
    // como saber que a pessoa digitou diferente do que pretendia.
    if (senha.nova !== senha.confirmacao) {
      setErroSenha('A nova senha e a confirmação não conferem.');
      return;
    }
    if (senha.nova.length < 8) {
      setErroSenha('A nova senha precisa ter ao menos 8 caracteres.');
      return;
    }

    setTrocando(true);
    try {
      await api.post('/auth/change-password', {
        current_password: senha.atual,
        new_password: senha.nova,
      });
      setSenha({ atual: '', nova: '', confirmacao: '' });
      setOkSenha('Senha alterada.');
    } catch (err) {
      setErroSenha(err.response?.data?.error || 'Não foi possível alterar a senha.');
    }
    setTrocando(false);
  }

  async function handleSave(e) {
    e.preventDefault(); setSuccess(''); setError('');
    // Nao envia os campos travados: mandar valor identico passa, mas mandar
    // qualquer coisa neles e ruido que o servidor teria de recusar.
    const payload = identidadeDoTenant
      ? { language: form.language }
      : form;
    try { await api.put('/profile', payload); setSuccess('Perfil atualizado'); checkAuth(); }
    catch (err) { setError(err.response?.data?.error || 'Erro'); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6"><h1 className="text-xl font-bold tracking-tight">Meu Perfil</h1></div>
      {success && <div className="p-3 rounded-md text-sm mb-4 bg-status-approved/10 text-status-approved border border-status-approved/20">{success}</div>}
      {error && <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">{error}</div>}

      <div className="bg-surface-100 border rounded-md p-6 max-w-[500px]">
        <form onSubmit={handleSave}>
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Nome</label>
            <input className={`form-input${identidadeDoTenant ? ' opacity-50' : ''}`} value={form.name}
              disabled={identidadeDoTenant}
              onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Email</label>
            <input className={`form-input${identidadeDoTenant ? ' opacity-50' : ''}`} type="email" value={form.email}
              disabled={identidadeDoTenant}
              onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>

          {identidadeDoTenant && (
            <p className="text-xs text-ink-muted -mt-2 mb-4">
              Nome e e-mail vem da sua conta Microsoft e sao atualizados a cada login.
              Para mudar, ajuste no Microsoft 365 e entre de novo. Eles identificam
              voce nas revisoes e aprovacoes que assina.
            </p>
          )}
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Idioma</label>
            <select className="form-select" value={form.language} onChange={(e) => setForm({ ...form, language: e.target.value })}>
              <option value="pt">Portugues</option><option value="en">Ingles</option><option value="es">Espanhol</option>
              <option value="ar">Arabe</option><option value="zh">Chines</option>
            </select></div>
          <div className="mb-4"><label className="block text-sm font-medium text-ink-secondary mb-1">Função</label>
            <input className="form-input opacity-50" value={roleLabel(user?.role)} disabled /></div>
          <button type="submit" className="btn btn-primary">Salvar</button>
        </form>
      </div>

      <div className="bg-surface-100 border rounded-md p-6 max-w-[500px] mt-4">
        <h2 className="text-base font-semibold mb-1">Senha</h2>

        {!temSenhaLocal ? (
          <p className="text-sm text-ink-secondary">
            Esta conta entra pelo Microsoft. A senha é gerenciada no Microsoft 365 e
            não pode ser alterada aqui.
          </p>
        ) : (
          <>
            <p className="text-xs text-ink-muted mb-4">
              Informe a senha atual para confirmar que é você. Mínimo de 8 caracteres.
            </p>

            {okSenha && (
              <div className="p-3 rounded-md text-sm mb-4 bg-status-approved/10 text-status-approved border border-status-approved/20">
                {okSenha}
              </div>
            )}
            {erroSenha && (
              <div className="p-3 rounded-md text-sm mb-4 bg-status-rejected/10 text-status-rejected border border-status-rejected/20">
                {erroSenha}
              </div>
            )}

            <form onSubmit={handleTrocarSenha}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="senha-atual">Senha atual</label>
                <input id="senha-atual" className="form-input" type="password" autoComplete="current-password"
                  value={senha.atual} onChange={(e) => setSenha({ ...senha, atual: e.target.value })} required />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="senha-nova">Nova senha</label>
                <input id="senha-nova" className="form-input" type="password" autoComplete="new-password"
                  value={senha.nova} onChange={(e) => setSenha({ ...senha, nova: e.target.value })} required minLength={8} />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-ink-secondary mb-1" htmlFor="senha-conf">Confirme a nova senha</label>
                <input id="senha-conf" className="form-input" type="password" autoComplete="new-password"
                  value={senha.confirmacao} onChange={(e) => setSenha({ ...senha, confirmacao: e.target.value })} required />
              </div>
              <button type="submit" className="btn btn-primary"
                disabled={trocando || !senha.atual || !senha.nova || !senha.confirmacao}>
                {trocando ? 'Alterando...' : 'Alterar senha'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
